import React, { FC, useEffect, useRef, useState } from "react";

import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";

import { FormDropdownInput } from "stentor-models";

import { FormFieldProps } from "../FormDisplay";

import styles from "./FormFieldDropdown.module.scss";

/**
 * Matches the mobile breakpoint used throughout the form widget stylesheets.
 */
export const MOBILE_MEDIA_QUERY = "(max-width: 640px)";

const PLACEHOLDER_LABEL = "Select an option";

/**
 * Extended SelectableItem interface to support free-form text input for dropdown items.
 * This allows dropdown options to trigger an additional text input field when selected.
 *
 * Note: This extends the stentor-models SelectableItem type with additional properties.
 * Consider adding these properties to stentor-models SelectableItem in a future release
 * if this pattern becomes widely used across the platform.
 */
export interface SelectableItemWithFreeForm {
    label: string;
    id: string;
    selected?: boolean;
    /**
     * If true, shows a free-form text input field when this item is selected.
     * Useful for "Other" options that require additional user input.
     */
    freeFormInput?: boolean;
    /**
     * Custom field name for the free-form input value.
     * Defaults to "message" if not specified.
     * This allows multiple dropdowns to have different free-form field names.
     */
    freeFormFieldName?: string;
}

export interface FormDropdownProps extends FormFieldProps {
    readonly field: FormDropdownInput;
}

export const FormFieldDropdown: FC<FormDropdownProps> = (props) => {
    const { field, onFieldChange, errorMessage, required, freeFormErrorMessage } = props;

    // At mobile widths render the native <select>: it gets the OS wheel/sheet picker,
    // correct momentum scrolling, and avoids focus-trap conflicts inside the fixed modal.
    // Desktop keeps the MUI listbox.
    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

    const [dropValue, setDropValue] = useState(() => {
        const selectedItem = field.items.find(item => item.selected);
        return selectedItem?.id || "none";
    });
    const [freeFormValue, setFreeFormValue] = useState("");
    const [showFreeForm, setShowFreeForm] = useState(() => {
        const selectedItem = field.items.find(item => item.selected) as SelectableItemWithFreeForm;
        return selectedItem?.freeFormInput || false;
    });

    const onChange = (e: SelectChangeEvent<string>) => {
        const { value } = e.target;

        // Get the previous selected item to clear its free-form value if needed
        const prevSelectedItem = field.items.find(
            (item) => item.id === dropValue
        ) as SelectableItemWithFreeForm;

        setDropValue(value);

        // Check if the selected item has freeFormInput enabled
        const selectedItem = field.items.find(
            (item) => item.id === value
        ) as SelectableItemWithFreeForm;
        if (selectedItem && selectedItem.freeFormInput) {
            setShowFreeForm(true);
        } else {
            setShowFreeForm(false);

            // Only clear if switching away from a free-form item AND there was actually a value
            if (prevSelectedItem?.freeFormInput && freeFormValue) {
                const messageFieldName = prevSelectedItem?.freeFormFieldName || "message";
                setFreeFormValue(""); // Clear local state
                onFieldChange(messageFieldName, ""); // Clear parent state
            }
        }
    };

    const onFreeFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFreeFormValue(e.target.value);
    };

    // Track onFieldChange reference to detect if it's not memoized in development
    const onFieldChangeRef = useRef(onFieldChange);
    useEffect(() => {
        if (process.env.NODE_ENV === "development" && onFieldChangeRef.current !== onFieldChange) {
            console.warn(
                "FormFieldDropdown: onFieldChange prop is not memoized. " +
                "This may cause performance issues. Please wrap it in useCallback in the parent component."
            );
        }
        onFieldChangeRef.current = onFieldChange;
    }, [onFieldChange]);

    // Get the current free-form field name if applicable
    const currentFreeFormFieldName = React.useMemo(() => {
        if (showFreeForm) {
            const selectedItem = field.items.find(
                (item) => item.id === dropValue
            ) as SelectableItemWithFreeForm;
            return selectedItem?.freeFormFieldName || "message";
        }
        return null;
    }, [showFreeForm, dropValue, field.items]);

    // IMPORTANT: Parent components MUST use useCallback for onFieldChange
    // to prevent infinite re-renders. See FormStepDisplay.tsx:352-373 for correct usage.
    useEffect(() => {
        // Send the raw dropdown ID value (formatting happens during text interpolation)
        if (dropValue !== "none") {
            onFieldChange(field.name, dropValue);
        }

        // If freeform is active and has a value, also send it to the configured field name
        if (showFreeForm && freeFormValue && currentFreeFormFieldName) {
            onFieldChange(currentFreeFormFieldName, freeFormValue);
        }
    }, [dropValue, freeFormValue, showFreeForm, field.name, currentFreeFormFieldName, onFieldChange]);

    return (
        <div className={styles.xapp_dropdown_container}>
            <FormControl fullWidth error={!!errorMessage} size="small">
                <InputLabel
                    id={`${field.name}-label`}
                    // The native <select> is labelled via htmlFor, and it always shows a
                    // value so the label has to stay shrunk out of the way.
                    htmlFor={isMobile ? field.name : undefined}
                    shrink={isMobile ? true : undefined}>
                    {field.title}
                </InputLabel>
                <Select
                    native={isMobile}
                    name={field.name}
                    id={field.name}
                    labelId={isMobile ? undefined : `${field.name}-label`}
                    value={dropValue}
                    onChange={onChange}
                    size="small"
                    displayEmpty
                    fullWidth
                    required={required}
                    label={field.title}>
                    {isMobile ? (
                        <option value="none">{PLACEHOLDER_LABEL}</option>
                    ) : (
                        <MenuItem value="none">
                            <em>{PLACEHOLDER_LABEL}</em>
                        </MenuItem>
                    )}
                    {field.items.map((item) => {
                        return isMobile ? (
                            <option key={item.id} value={item.id}>
                                {item.label}
                            </option>
                        ) : (
                            <MenuItem key={item.id} value={item.id}>
                                {item.label}
                            </MenuItem>
                        );
                    })}
                </Select>
                {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
            </FormControl>

            {showFreeForm && (
                <TextField
                    fullWidth
                    size="small"
                    required
                    placeholder="Please specify"
                    value={freeFormValue}
                    onChange={onFreeFormChange}
                    style={{ marginTop: "8px" }}
                    error={!!freeFormErrorMessage}
                    helperText={freeFormErrorMessage}
                    slotProps={field.maxLength ? { htmlInput: { maxLength: field.maxLength } } : undefined}
                />
            )}
        </div>
    );
};

export default FormFieldDropdown;
